import * as yup from "yup";
import { useInfiniteQuery } from "@tanstack/react-query";
import { HTTP_ENDPOINTS, QUERY_KEYS } from "../../consts/api";
import { http } from "../../services/HttpService";
import { removeNullishFromShape } from "../../utilities/general";
import {
  composePaginationSchema,
  getNextPageParam,
  paginationSchema,
} from "../../schemas/pagination";

export const ORDER_BY = {
  order: "bi_order",
  clientName: "bi_client_name",
  total: "bi_total",
  paymentStatus: "bi_payment_status",
  fulfillmentStatus: "bi_fulfillment_status",
  date: "bi_date",
};

const requestSchema = yup.object({
  patientId: yup.number().required(),
  pageSize: yup.number().required(),
  search: yup.string().nullable(),
  order: yup
    .object({
      by: yup.string().required(),
      direction: yup.string().required(),
    })
    .nullable(),
});

const responseSchema = paginationSchema.concat(
  yup.object({
    reports: yup
      .array()
      .of(
        yup.object({
          bi_order: yup.string().required(),
          bi_client_name: yup.string().required(),
          bi_email_phone: yup.string().required(),
          bi_email: yup.string().required(),
          bi_phone: yup.string().required(),
          bi_total: yup.string().required(),
          bi_payment_status: yup.string().required(),
          bi_fulfillment_status: yup.string().required(),
          bi_items: yup.number().required(),
          bi_date: yup.string().required(),
        }),
      )
      .required(),
  }),
);

const composeResponse = (res, req) => {
  const reports = res?.data?.data?.data || [];

  return {
    ...composePaginationSchema({
      response: res?.data?.data || {},
      pageSize: req.pageSize,
    }),
    reports,
  };
};

function createShopifyPatientOrdersReportQueryKey(...args) {
  return [QUERY_KEYS.shopifyPatientOrders, ...args];
}

export function useShopifyPatientOrdersReportQuery({
  payload = {},
  options = {},
} = {}) {
  const req = requestSchema.validateSync(payload, {
    strict: true,
  });

  return useInfiniteQuery({
    queryKey: createShopifyPatientOrdersReportQueryKey(
      req.search,
      req.order?.by,
      req.order?.direction,
    ),
    queryFn: async ({ pageParam = {} }) => {
      const res = await http.get(HTTP_ENDPOINTS.getShopifyPatientOrders(), {
        params: removeNullishFromShape({
          ...pageParam,
          patient_id: req.patientId,
          pagesize: req.pageSize,
          term: req.search || undefined,
          sortOrder: req.order?.direction,
          sortBy: req.order?.by,
        }),
      });

      return responseSchema.validateSync(composeResponse(res, req), {
        strict: true,
      });
    },
    ...options,
    getNextPageParam: getNextPageParam,
  });
}
